import { useCallback, useEffect, useRef } from "react";
import type { MutableRefObject } from "react";
import { audioNotification } from "@components/Common/Notifications/audioNotification";
import type { DataPipeline } from "../../DataPipeline";
import { serializeDashboardSpec } from "../serializeSpec";
import type { BackendDashboardSpec, DashboardDataResponseSpec } from "../../Processing/types";
import type { DashboardState, WidgetEntry } from "../dashboardState";

/**
 * Debounced auto-save: persists the dashboard 800ms after the last widget/layout
 * mutation. A failed save or refetch sets the stale flag again, so the next
 * auto-save retries the data refetch.
 */
export function useAutoSave(opts: {
  state: DashboardState;
  canEdit: boolean;
  pipeline: DataPipeline;
  dataStaleRef: MutableRefObject<boolean>;
  justLoadedRef: MutableRefObject<boolean>;
  refreshData: (savedSpec?: BackendDashboardSpec) => Promise<DashboardDataResponseSpec | null>;
  requestAllAiWidgets: (widgets: Record<string, WidgetEntry>) => void;
  /** Ends the corner spinners on controller dependents when no refetch will follow. */
  clearRefreshing: () => void;
}) {
  const { state, canEdit, pipeline, dataStaleRef, justLoadedRef, refreshData, requestAllAiWidgets, clearRefreshing } = opts;
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // The save the timer will run, so flushSave can run it early.
  const pendingSaveRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (!state.dashboardId || !canEdit) return;

    if (justLoadedRef.current) {
      justLoadedRef.current = false;
      return;
    }

    clearTimeout(saveTimerRef.current);

    const save = async () => {
      pendingSaveRef.current = null;
      // Consumed now, not after the save: an edit that lands while this save is
      // in flight sets the flag again, and the next save must still see it.
      const shouldRefetch = dataStaleRef.current;
      dataStaleRef.current = false;
      try {
        const payload = serializeDashboardSpec(state);
        await pipeline.saveDashboard(payload);
        if (shouldRefetch) {
          const dataResponse = await refreshData(payload as unknown as BackendDashboardSpec);
          // null is a failed or superseded refetch. The AI tiles must not
          // re-query against the previous data; the next save refetches.
          if (!dataResponse) {
            dataStaleRef.current = true;
            return;
          }
          // The dashboard's data just changed, so every AI tile's answer is
          // stale — including tiles whose own config didn't change (a filter on
          // a neighbouring widget changes what they're describing). The save
          // landed first, so the stored spec is what the backend will read.
          requestAllAiWidgets(state.widgets);
        } else {
          clearRefreshing();
        }
      } catch (err) {
        if (shouldRefetch) dataStaleRef.current = true;
        clearRefreshing();
        audioNotification.error({
          message: "Save failed",
          description: "Failed to save dashboard changes. Please try again.",
          placement: "top",
          duration: 5,
        });
      }
    };
    pendingSaveRef.current = save;
    saveTimerRef.current = setTimeout(save, 800);

    return () => clearTimeout(saveTimerRef.current);
  }, [state.widgets, state.layout, state.dashboardId, canEdit, pipeline, refreshData, requestAllAiWidgets, clearRefreshing]);

  /**
   * Runs the debounced save now, if one is pending. The PDF export awaits it, so
   * the server renders the state on screen and not the one saved 800 ms ago.
   */
  const flushSave = useCallback(async () => {
    const save = pendingSaveRef.current;
    if (!save) return;
    clearTimeout(saveTimerRef.current);
    await save();
  }, []);

  return { flushSave };
}
